import type { RequestInfo } from '@warp-drive/core/types/request';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildBaseURL } from '@warp-drive/utilities';
import { query } from '@warp-drive/utilities/json-api';
import type { RecommendationPromptVersion } from '../data/resources/recommendation-prompt-version.type.ts';
import { CONTRACT_HEADER } from '../handlers/type-normalization-handler.ts';
import { SkipErrorReporting } from '../request-options.ts';
import { parseCommandResult } from './command-result.ts';

export interface InternalAiContextDocument {
  title: string;
  content: string;
}

export interface InternalAiContentResult {
  response: string;
  documentsUsed: string[];
}

export function queryInternalAiContentRequest(
  query: string,
  contextDocuments: InternalAiContextDocument[]
): RequestInfo {
  return {
    url: buildBaseURL({ resourcePath: 'api/v2/internal-ai/content-query' }),
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
    body: JSON.stringify({
      internal_ai_query: {
        query,
        context_documents: contextDocuments,
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

export function parseInternalAiContentResult(content: unknown) {
  return parseCommandResult<InternalAiContentResult>(content);
}

/**
 * Builds the query for the exact prompt version a recommendation used, by the
 * identity the agent persisted into its context (`db:<set_key>:v<n>`).
 *
 * The identity carries both `:` and `/` — a scoped set_key is
 * `<company_id>/<program_code>` — so it is encoded rather than interpolated
 * raw.
 *
 * `SkipErrorReporting` because every failure here is expected and handled by
 * the caller: the endpoint is internal-staff-only, so a partner admin gets a
 * 403 that must not raise an "Access denied" toast or touch the session, and a
 * withdrawn version 404s as a normal outcome.
 *
 * @param identity The stored provenance identity; scoped values include `/`.
 * @returns A target-contract request that materializes one prompt-version resource.
 */
export function recommendationPromptVersionQuery(identity: string): RequestInfo {
  const request = query<RecommendationPromptVersion>(
    'recommendation-prompt-version',
    { identity },
    { resourcePath: 'api/v2/recommendation-prompts/by-identity' }
  );
  const headers = new Headers(request.headers);
  headers.set(CONTRACT_HEADER, '2');

  return {
    ...request,
    headers,
    cacheOptions: { reload: true },
    options: { [SkipErrorReporting]: true },
  };
}
